import { BigNumber } from 'ethers';
import Web3 from 'web3';

import { DEFAULTS } from 'constants/defaults.constant';

/**
 * Base class for Web3 services
 * Provides common functionality for all Web3-related services including gas management, utilities, and contract creation
 */
export abstract class BaseWeb3Service {
  protected readonly gasMultiplier: number;
  private torusProvider?: any;
  private contractCache: Map<string, any>;

  // Security: Maximum allowed gas price multiplier to prevent excessive gas costs
  private readonly MAX_GAS_MULTIPLIER = 5;

  constructor(protected web3: Web3, torusProvider?: any) {
    const envMultiplier = process.env.REACT_APP_GAS_PRICE_MULTIPLIER;
    this.gasMultiplier = envMultiplier ? +envMultiplier : 1;
    this.torusProvider = torusProvider;
    this.contractCache = new Map<string, any>();
  }

  /**
   * Updates network provider (Torus transaction error workaround)
   */
  protected async updateNetwork(): Promise<void> {
    if (this.torusProvider) {
      const network = JSON.parse(process.env.REACT_APP_TORUS!);
      await this.torusProvider.setProvider({
        ...network,
      });
    }
  }

  /**
   * Gets the current gas price with multiplier applied
   * @throws Error if multiplier exceeds maximum allowed value
   */
  protected async getGasPrice(customMultiplier?: number): Promise<string> {
    const multiplier = this.getGasPriceMultiplier(customMultiplier);

    // Security: Validate gas multiplier to prevent excessive gas costs
    if (multiplier > this.MAX_GAS_MULTIPLIER) {
      throw new Error(`Gas multiplier ${multiplier} exceeds maximum allowed value of ${this.MAX_GAS_MULTIPLIER}`);
    }

    const gasPrice = await this.web3.eth.getGasPrice();

    return BigNumber.from(gasPrice).mul(multiplier).toString();
  }

  /**
   * Validates and returns the gas price multiplier
   */
  protected getGasPriceMultiplier(multiplier: number = this.gasMultiplier): number {
    const effectiveMultiplier = multiplier ?? this.gasMultiplier;

    if (!effectiveMultiplier || Number.isNaN(effectiveMultiplier) || Number.isNaN(+effectiveMultiplier)) {
      return 1;
    }

    return effectiveMultiplier;
  }

  /**
   * Creates a contract instance with caching for improved performance
   * Cached contracts are reused for the same address to avoid repeated instantiation
   * @param abi - Contract ABI
   * @param address - Contract address
   * @param useCache - Whether to use cached contract (default: true)
   */
  protected createContract(abi: any[], address: string, useCache: boolean = true): any {
    if (!useCache) {
      return new this.web3.eth.Contract(abi, address);
    }

    const cacheKey = `${address.toLowerCase()}`;

    if (this.contractCache.has(cacheKey)) {
      return this.contractCache.get(cacheKey);
    }

    const contract = new this.web3.eth.Contract(abi, address);
    this.contractCache.set(cacheKey, contract);

    return contract;
  }

  /**
   * Clears the contract cache
   * Useful when switching networks or providers
   */
  protected clearContractCache(): void {
    this.contractCache.clear();
  }

  /**
   * Waits for transaction confirmation
   * Returns true when the default number of confirmation blocks is reached
   */
  protected isConfirmed(blocks: number): boolean {
    return blocks === DEFAULTS.defaultConfirmationBlocks;
  }

  /**
   * Computes keccak256 hash of a string
   */
  protected keccak256(value: string): string {
    return this.web3.utils.keccak256(value);
  }

  /**
   * Converts a value to hex
   */
  protected toHex(value: string | number): string {
    return this.web3.utils.toHex(value);
  }
}
